import React from 'react'
import { Input, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { Trans, useTranslation } from 'react-i18next'
import {
  DC4_TRAINING_AGENT_TYPES,
  DC4_TRAINING_MODALITIES,
  DC4_TRAINING_OBJECTIVES,
  Dc4CatalogItem,
} from 'config/dc4Catalogs'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import FormErrorMessage from 'components/reusables/formErrorMessage'
import {
  Dc4FieldsCard,
  Field,
  FieldControl,
  InfoBanner,
  InfoBannerContent,
  InfoBannerIcon,
  InfoBannerText,
} from './styled'

const { Option } = Select

export interface Dc4FieldValues {
  trainingAgentRfc: string
  dc4TrainingAgentType: number | null
  dc4TrainingModality: number | null
  dc4TrainingObjective: number | null
}

type Dc4SelectField = Exclude<keyof Dc4FieldValues, 'trainingAgentRfc'>

interface Props {
  values: Dc4FieldValues
  errors: Partial<Record<keyof Dc4FieldValues, string>>
  onChange: (field: keyof Dc4FieldValues, value: string | number | null) => void
}

const Dc4Fields: React.FC<Props> = ({ values, errors, onChange }) => {
  const { t } = useTranslation()
  const prefix = 'administration.certificate.legalAttestationForm.dc4'

  const renderSelect = (
    field: Dc4SelectField,
    labelKey: string,
    options: readonly Dc4CatalogItem[],
    testId: string
  ) => (
    <Field>
      <RequiredFieldLabel required>{t(labelKey)}</RequiredFieldLabel>
      <FieldControl $hasError={!!errors[field]}>
        <Select
          value={values[field] ?? undefined}
          placeholder={t(`${prefix}.selectPlaceholder`)}
          onChange={(next: number | undefined) => onChange(field, next ?? null)}
          style={{ width: '100%' }}
          data-testid={testId}
        >
          {options.map((item) => (
            <Option key={item.value} value={item.value}>
              {t(item.labelKey)}
            </Option>
          ))}
        </Select>
      </FieldControl>
      <FormErrorMessage message={errors[field]} />
    </Field>
  )

  return (
    <Dc4FieldsCard>
      <InfoBanner>
        <InfoBannerIcon>
          <FontAwesomeIcon icon={faInfoCircle} />
        </InfoBannerIcon>
        <InfoBannerContent>
          <InfoBannerText>
            <Trans
              i18nKey={`${prefix}.banner`}
              components={{ strong: <strong /> }}
            />
          </InfoBannerText>
        </InfoBannerContent>
      </InfoBanner>
      <Field>
        <RequiredFieldLabel required>
          {t(`${prefix}.agentRfc.label`)}
        </RequiredFieldLabel>
        <FieldControl $hasError={!!errors.trainingAgentRfc}>
          <Input
            value={values.trainingAgentRfc}
            onChange={(event) =>
              onChange('trainingAgentRfc', event.target.value)
            }
            data-testid="dc4-training-agent-rfc"
          />
        </FieldControl>
        <FormErrorMessage message={errors.trainingAgentRfc} />
      </Field>
      {renderSelect(
        'dc4TrainingAgentType',
        `${prefix}.agentType.label`,
        DC4_TRAINING_AGENT_TYPES,
        'dc4-training-agent-type'
      )}
      {renderSelect(
        'dc4TrainingModality',
        `${prefix}.modality.label`,
        DC4_TRAINING_MODALITIES,
        'dc4-training-modality'
      )}
      {renderSelect(
        'dc4TrainingObjective',
        `${prefix}.objective.label`,
        DC4_TRAINING_OBJECTIVES,
        'dc4-training-objective'
      )}
    </Dc4FieldsCard>
  )
}

export default Dc4Fields
